import { useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCompanyUsersQuery,
  useRevokeCompanyInviteMutation,
  useRemoveCompanyUserMutation,
} from './hooks';
import type { TCompanyUser, TCompanyUserStatus, TCompanyPolicy } from 'librechat-data-provider';
import {
  Avatar,
  Button,
  Chip,
  FilterBar,
  FilterSelect,
  Notice,
  PageHeader,
  Panel,
  Section,
} from './Chrome';
import { formatCount, formatDate, initialsOf } from './format';
import { errorMessage } from './errors';
import { Icon } from './Icons';

const PAGE_SIZE = 25;

const STATUS_CHIP: Record<TCompanyUserStatus, { background: string; color: string }> = {
  active: { background: 'var(--lh-lgreen)', color: 'var(--lh-ink)' },
  signed_up: { background: 'var(--lh-lblue)', color: 'var(--lh-ink)' },
  invited: { background: 'var(--lh-apricot)', color: 'var(--lh-apricot-ink)' },
};

const STATUS_LABEL: Record<TCompanyUserStatus, string> = {
  active: 'com_ui_leadhr_status_active',
  signed_up: 'com_ui_leadhr_status_signed_up',
  invited: 'com_ui_leadhr_status_invited',
};

const STATUSES: TCompanyUserStatus[] = ['invited', 'signed_up', 'active'];

export default function Users({
  groupId,
  policy,
  initialStatus = '',
}: {
  groupId: string;
  policy?: TCompanyPolicy;
  initialStatus?: TCompanyUserStatus | '';
}): ReactElement {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TCompanyUserStatus | ''>(initialStatus);
  const [region, setRegion] = useState('');
  const [managementLevel, setManagementLevel] = useState('');
  const [page, setPage] = useState(0);
  const [actionError, setActionError] = useState<string | undefined>();

  const params = useMemo(
    () => ({
      search: search || undefined,
      status: status || undefined,
      region: region || undefined,
      managementLevel: managementLevel || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, status, region, managementLevel, page],
  );

  const { data, isLoading } = useCompanyUsersQuery(groupId, params);
  const revoke = useRevokeCompanyInviteMutation();
  const remove = useRemoveCompanyUserMutation();

  const rows = data?.users ?? [];
  const total = data?.total ?? 0;
  const seats = data?.seats;
  const lastPage = Math.max(Math.ceil(total / PAGE_SIZE) - 1, 0);

  /** Any filter change invalidates the current offset — page 3 of a new filter is empty. */
  const resetTo =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setPage(0);
    };

  const handleRemove = (row: TCompanyUser) => {
    setActionError(undefined);
    const onError = (error: Error) =>
      setActionError(errorMessage(error, t('com_ui_leadhr_action_failed')));
    if (row.status === 'invited') {
      revoke.mutate({ groupId, email: row.email }, { onError });
      return;
    }
    if (!row.id) {
      return;
    }
    remove.mutate({ groupId, userId: row.id }, { onError });
  };

  const regions = policy?.regions ?? [];
  const levels = policy?.managementLevels ?? [];

  const choices = (label: string, values: string[]) => [
    { value: '', label },
    ...values.map((value) => ({ value, label: value })),
  ];

  const subtitle = !seats
    ? t('com_ui_leadhr_licences_subtitle_plain')
    : seats.total === null
      ? t('com_ui_leadhr_licences_subtitle_unlimited', { used: formatCount(seats.used) })
      : t('com_ui_leadhr_licences_subtitle', {
          used: formatCount(seats.used),
          total: formatCount(seats.total),
        });

  return (
    <>
      <PageHeader title={t('com_ui_leadhr_licences_title')} subtitle={subtitle} />

      <FilterBar>
        <FilterSelect
          id="leadhr-status"
          label={t('com_ui_leadhr_col_status')}
          value={status}
          options={[
            { value: '', label: t('com_ui_leadhr_filter_all') },
            ...STATUSES.map((value) => ({ value, label: t(STATUS_LABEL[value]) })),
          ]}
          onChange={(value) => resetTo(setStatus)(value as TCompanyUserStatus | '')}
        />
        {regions.length > 0 ? (
          <FilterSelect
            id="leadhr-region"
            label={t('com_ui_leadhr_filter_region')}
            value={region}
            options={choices(t('com_ui_leadhr_filter_all_regions'), regions)}
            onChange={resetTo(setRegion)}
          />
        ) : null}
        {levels.length > 0 ? (
          <FilterSelect
            id="leadhr-level"
            label={t('com_ui_leadhr_filter_level')}
            value={managementLevel}
            options={choices(t('com_ui_leadhr_filter_all_levels'), levels)}
            onChange={resetTo(setManagementLevel)}
          />
        ) : null}
      </FilterBar>

      <Section
        title={t('com_ui_leadhr_users_title')}
        subtitle={t('com_ui_leadhr_users_subtitle')}
        aside={
          <div
            className="flex h-10 min-w-[260px] items-center gap-2 rounded-lg px-3.5"
            style={{ border: '1px solid var(--lh-line)', background: 'var(--lh-card)' }}
          >
            <span style={{ color: 'var(--lh-muted)' }}>
              <Icon name="search" size={16} />
            </span>
            <input
              type="search"
              value={search}
              onChange={(event) => resetTo(setSearch)(event.target.value)}
              placeholder={t('com_ui_leadhr_search_users')}
              aria-label={t('com_ui_leadhr_search_users')}
              className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 outline-none"
              style={{ font: 'inherit', color: 'var(--lh-ink)' }}
            />
          </div>
        }
      >
        {actionError ? <Notice>{actionError}</Notice> : null}

        <Panel className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr>
                <HeaderCell>{t('com_ui_leadhr_col_user')}</HeaderCell>
                <HeaderCell>{t('com_ui_leadhr_col_unit')}</HeaderCell>
                <HeaderCell>{t('com_ui_leadhr_col_region')}</HeaderCell>
                <HeaderCell>{t('com_ui_leadhr_col_level')}</HeaderCell>
                <HeaderCell>{t('com_ui_leadhr_col_since')}</HeaderCell>
                <HeaderCell>{t('com_ui_leadhr_col_status')}</HeaderCell>
                <HeaderCell align="right">
                  <span className="sr-only">{t('com_ui_leadhr_col_actions')}</span>
                </HeaderCell>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <EmptyRow message={t('com_ui_leadhr_loading')} />
              ) : rows.length === 0 ? (
                <EmptyRow message={t('com_ui_leadhr_no_users')} />
              ) : (
                rows.map((row) => (
                  <tr key={row.id ?? row.email} style={{ borderTop: '1px solid var(--lh-rule)' }}>
                    <BodyCell>
                      <div className="flex items-center gap-3">
                        <Avatar initials={initialsOf(row.name, row.email)} />
                        <div className="flex min-w-0 flex-col leading-snug">
                          <span>{row.name}</span>
                          <span className="lh-small" style={{ color: 'var(--lh-muted)' }}>
                            {row.email}
                          </span>
                        </div>
                      </div>
                    </BodyCell>
                    <BodyCell>{row.unit ?? '—'}</BodyCell>
                    <BodyCell>{row.region ?? '—'}</BodyCell>
                    <BodyCell>{row.managementLevel ?? '—'}</BodyCell>
                    <BodyCell muted>{formatDate(row.since)}</BodyCell>
                    <BodyCell>
                      <Chip dot {...STATUS_CHIP[row.status]}>
                        {t(STATUS_LABEL[row.status])}
                      </Chip>
                    </BodyCell>
                    <BodyCell align="right" last>
                      <Button
                        size="small"
                        onClick={() => handleRemove(row)}
                        disabled={revoke.isLoading || remove.isLoading}
                      >
                        {row.status === 'invited'
                          ? t('com_ui_leadhr_revoke')
                          : t('com_ui_leadhr_remove')}
                      </Button>
                    </BodyCell>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div
            className="flex items-center justify-between gap-4 pt-4"
            style={{ borderTop: '1px solid var(--lh-rule)' }}
          >
            <span className="lh-small" style={{ color: 'var(--lh-muted)' }}>
              {t('com_ui_leadhr_showing', {
                shown: formatCount(rows.length),
                total: formatCount(total),
              })}
            </span>
            <div className="flex gap-2">
              <PageButton
                label={t('com_ui_leadhr_prev_page')}
                onClick={() => setPage((p) => Math.max(p - 1, 0))}
                disabled={page === 0}
              >
                ‹
              </PageButton>
              <PageButton
                label={t('com_ui_leadhr_next_page')}
                onClick={() => setPage((p) => Math.min(p + 1, lastPage))}
                disabled={page >= lastPage}
              >
                ›
              </PageButton>
            </div>
          </div>
        </Panel>
      </Section>
    </>
  );
}

function HeaderCell({
  children,
  align = 'left',
}: {
  children: ReactNode;
  align?: 'left' | 'right';
}): ReactElement {
  return (
    <th
      className="pb-2 pr-3 text-xs font-normal last:pr-0"
      style={{ textAlign: align, color: 'var(--lh-muted)' }}
    >
      {children}
    </th>
  );
}

function BodyCell({
  children,
  muted,
  align = 'left',
  last,
}: {
  children: ReactNode;
  muted?: boolean;
  align?: 'left' | 'right';
  last?: boolean;
}): ReactElement {
  return (
    <td
      className={`py-3 ${last ? '' : 'pr-3'}`}
      style={{ textAlign: align, color: muted ? 'var(--lh-muted)' : undefined }}
    >
      {children}
    </td>
  );
}

function EmptyRow({ message }: { message: string }): ReactElement {
  return (
    <tr>
      <td
        colSpan={7}
        className="py-10 text-center"
        style={{ color: 'var(--lh-muted)', borderTop: '1px solid var(--lh-rule)' }}
      >
        {message}
      </td>
    </tr>
  );
}

function PageButton({
  children,
  label,
  onClick,
  disabled,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  disabled: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="lh-btn lh-btn--small !w-9 !p-0"
    >
      {children}
    </button>
  );
}
